import { Lock, LogOut, Menu, ShieldCheck, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import GoogleTranslate from '@/components/GoogleTranslate';
import { useAuth } from '@/contexts/AuthContext';
import { SUBPAGES } from '@/pages/MemberApp';

const MobileTopBar = ({ active, onSelect, drawerOpen, setDrawerOpen, me }) => {
  const navigate = useNavigate();
  const activeMeta = SUBPAGES.find((s) => s.id === active);
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      {/* Sticky top bar (mobile only) */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 px-4 flex items-center justify-between bg-[rgba(8,8,9,0.92)] backdrop-blur-xl border-b border-[rgba(255,182,18,0.12)]"
        data-testid="mobile-topbar"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#FFC20E] to-[#FFB612] shrink-0">
            <span className="text-[#0a0a0b] font-black font-display text-sm">1S</span>
          </div>
          <div className="font-display font-bold text-sm shrink-0">
            <span className="sc-gold-text">3.0</span>
          </div>
          <span className="text-[12px] text-[var(--sc-text-dim)] ml-1 truncate min-w-0">/ {activeMeta?.label}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <GoogleTranslate />
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="w-9 h-9 rounded-lg border border-[rgba(255,182,18,0.32)] flex items-center justify-center text-[#FFC20E]"
            aria-label="Open menu"
            data-testid="hamburger-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <aside className="absolute right-0 top-0 bottom-0 w-[280px] max-w-[88vw] bg-[rgba(8,8,9,0.96)] border-l border-[rgba(255,182,18,0.18)] backdrop-blur-xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[rgba(255,182,18,0.10)]">
              <div className="font-display font-bold">Menu</div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 rounded-lg border border-[rgba(255,182,18,0.32)] flex items-center justify-center text-[#FFC20E]"
                aria-label="Close menu"
                data-testid="drawer-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-4 border-b border-[rgba(255,182,18,0.08)]">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Signed in as</div>
              <div className="font-display font-bold mt-1">{me.username}</div>
              <div className="text-[11px] text-[var(--sc-text-dim)]">{me.email}</div>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              {SUBPAGES.map((s) => {
                // Mirror desktop Sidebar lock: Purchase/Deposit gated on
                // Step 1 (sponsor verification) completion.
                const locked = s.id === 'purchase' && me && !me.sponsor_verified;
                const handleClick = () => {
                  if (locked) {
                    toast.error('Complete Step 1: Verify Your Sponsor first.', {
                      description: 'Open the Dashboard and confirm your sponsor before depositing.',
                    });
                    return;
                  }
                  onSelect(s.id);
                };
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={handleClick}
                    aria-disabled={locked ? 'true' : 'false'}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-left text-[14px] transition ${
                      active === s.id
                        ? 'bg-[rgba(255,182,18,0.10)] border border-[rgba(255,182,18,0.32)] text-[#FFC20E] font-semibold'
                        : locked
                          ? 'text-[var(--sc-text-mute)] border border-transparent opacity-55 cursor-not-allowed'
                          : 'text-[var(--sc-text-dim)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] border border-transparent'
                    }`}
                    data-testid={`mobile-nav-${s.id}`}
                    data-locked={locked ? 'true' : 'false'}
                  >
                    <span className="flex-1 truncate">{s.label}</span>
                    {locked ? <Lock className="w-3.5 h-3.5 shrink-0 text-[var(--sc-text-mute)]" /> : null}
                  </button>
                );
              })}
            </nav>

            <div className="px-3 pb-4 pt-2 border-t border-[rgba(255,182,18,0.08)] space-y-1">
              {me?.is_admin ? (
                <button
                  type="button"
                  onClick={() => navigate('/admin', { replace: false })}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-[13px] font-semibold text-[#FFC20E] bg-[rgba(255,182,18,0.06)] border border-[rgba(255,182,18,0.28)] hover:bg-[rgba(255,182,18,0.10)] transition"
                  data-testid="mobile-nav-admin"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Open Admin Console
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-[13px] text-[var(--sc-text-mute)] hover:text-[#FFC20E] hover:bg-[rgba(255,182,18,0.04)] transition"
              >
                <LogOut className="w-4 h-4" />
                Switch / Sign out
              </button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default MobileTopBar;
